import { format, isValid, parseISO } from "date-fns";
import React, { useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";

import { faEllipsisV } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonIcon, Chip, Link, Text } from "@performyard/py-components";

import FloatingDropdown from "./FloatingDropdown";

import type { MeetingGoalAction } from "../types";

import type { Goal } from "../../../../api/services/searchServiceResultTypes/goal";

import { parseGoalPermissionsForUser } from "../../../Goals/Single/utils";
import { getProgressChartData, getProgressChartOptions } from "../utils";
import "../utils/chart-setup";

import GoalConstants from "../../../../constants/Goals";

type MeetingGoalActionOption = {
	id: MeetingGoalAction;
	label: string;
};

export const getMeetingGoalActionOptions = (
	goal: Goal,
	permissions: string[] = [],
	optionFlags: { includeAddToAgenda?: boolean } = {}
): MeetingGoalActionOption[] => {
	const parsedPermissions = parseGoalPermissionsForUser(permissions);
	const isActive = goal.status === "active";
	const options: MeetingGoalActionOption[] = [];

	if (optionFlags.includeAddToAgenda) {
		options.push({ id: "addToAgenda", label: "Add to Agenda" });
	}

	if (parsedPermissions.userCanEditDetails && isActive) {
		options.push({ id: "edit", label: "Edit Goal" });
	}

	if (parsedPermissions.userCanEditValue && isActive) {
		options.push({ id: "update", label: "Update Progress" });
	}

	if (parsedPermissions.userCanAddGoalsComments) {
		options.push({ id: "addMessage", label: "Add Message" });
	}

	if (parsedPermissions.userCanEditStatus && isActive) {
		options.push({ id: "complete", label: "Mark As Complete" });
	}

	if (parsedPermissions.userCanEditStatus) {
		options.push({ id: isActive ? "archive" : "reopen", label: isActive ? "Archive" : "Reopen" });
	}

	return options;
};

type MeetingGoalListItemProps = {
	goal: Goal;
	onActionSelect: (action: MeetingGoalAction, goal: Goal) => void;
	isInteractionDisabled?: boolean;
	includeAddToAgenda?: boolean;
};

const MeetingGoalListItem = ({
	goal,
	onActionSelect,
	isInteractionDisabled = false,
	includeAddToAgenda = false,
}: MeetingGoalListItemProps): React.ReactElement => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const options = useMemo(
		() => getMeetingGoalActionOptions(goal, goal.permissions, { includeAddToAgenda }),
		[goal, includeAddToAgenda]
	);
	const canViewDetails = goal.permissions?.includes("goals:viewDetails");
	const goalHref = `/goal/${goal._id}/`;
	const statusLabel =
		GoalConstants.statusLabels?.[goal.status as keyof typeof GoalConstants.statusLabels] || goal.status;
	const dueDate =
		goal.endDate && isValid(parseISO(goal.endDate)) ? format(parseISO(goal.endDate), "MMMM dd, yyyy") : "";

	return (
		<div
			className="flex items-start gap-4 border-b border-solid border-neutral-2 p-4"
			data-testid={`meeting-goal-${goal._id}`}
		>
			<div className="flex h-8 w-8 items-center justify-center">
				<Doughnut
					width={32}
					options={getProgressChartOptions()}
					data={getProgressChartData(goal.progress || 0, goal.outlook?.color)}
				/>
			</div>
			<div className="flex min-w-0 flex-1 flex-col gap-1">
				{canViewDetails ? (
					<Link
						href={goalHref}
						forceHardReload={true}
						dangerouslySetClassName="w-fit no-goal-tooltip no-underline hover:underline"
					>
						<Text size="paragraph-3" weight="semi-bold" color="primary-5">
							{goal.name}
						</Text>
					</Link>
				) : (
					<Text size="paragraph-3" weight="semi-bold">
						{goal.name}
					</Text>
				)}
				<div className="flex flex-wrap items-center gap-1">
					<Text size="caption" weight="regular">
						{goal.assignee?.name}
					</Text>
					{dueDate ? (
						<>
							<Text size="caption" weight="regular">
								•
							</Text>
							<Text size="caption" weight="regular">
								{`Due: ${dueDate}`}
							</Text>
						</>
					) : null}
				</div>
				<div className="w-fit">
					<Chip label={`Status: ${statusLabel}`} color="neutral" size={20} />
				</div>
			</div>
			{options.length > 0 ? (
				<FloatingDropdown
					isOpen={isMenuOpen}
					onOpenChange={setIsMenuOpen}
					onSelect={(option) => onActionSelect(option.id, goal)}
					trigger={
						<ButtonIcon
							icon={faEllipsisV}
							size={16}
							disabled={isInteractionDisabled}
							aria-label="Goal options"
						/>
					}
					options={options.map((option) => ({
						id: option.id,
						label: (
							<Text size="caption" weight="regular" color="neutral-5">
								{option.label}
							</Text>
						),
					}))}
				/>
			) : null}
		</div>
	);
};

export default MeetingGoalListItem;
